/**
 * Email — Needs Assignment (TRUS-224)
 *
 * Triage queue for emails synced from a user's personal Outlook/Gmail mailbox
 * that could not be auto-linked to a member. The user searches for a member and
 * assigns the email; on assign it is recorded and mirrored into the member Inbox.
 */

import React, { useEffect, useState } from 'react';
import { Mail, RefreshCw, UserPlus, Search, CheckCircle } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { inboxEmailAPI, type NeedsAssignmentEmail, type MailboxStatus } from '@/services/api/inboxEmail';
import { oauthAPI } from '@/services/api/oauth';

interface MemberResult {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export const EmailNeedsAssignment: React.FC = () => {
  const [mailbox, setMailbox] = useState<MailboxStatus | null>(null);
  const [items, setItems] = useState<NeedsAssignmentEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeAssign, setActiveAssign] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemberResult[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [status, queue] = await Promise.all([
        inboxEmailAPI.status().catch(() => null),
        inboxEmailAPI.needsAssignment().catch(() => ({ items: [], count: 0 })),
      ]);
      setMailbox(status);
      setItems(queue.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await inboxEmailAPI.syncNow();
      // Give the worker a moment, then refresh
      setTimeout(load, 2500);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Sync unavailable');
    } finally {
      setSyncing(false);
    }
  };

  // Email-only consent. Requesting the four mail scopes rather than all seven keeps
  // the tenant-admin approval as small as possible — over-asking is the usual reason a
  // Microsoft 365 admin refuses the app outright.
  const connectOutlook = async () => {
    setConnecting(true);
    try {
      const { auth_url } = await oauthAPI.microsoft.authorize(window.location.pathname, 'email');
      if (auth_url) {
        window.location.href = auth_url;
        return;
      }
      alert('Could not start the Microsoft connection. Please try Settings → Connected Services.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Could not start the Microsoft connection.');
    } finally {
      setConnecting(false);
    }
  };

  const searchMembers = async (q: string) => {
    setQuery(q);
    if (q.length < 2) {
      setResults([]);
      return;
    }
    try {
      const res = await apiClient.get('/api/members/search/unified', { params: { q, limit: 15 } });
      setResults(res.data?.members || []);
    } catch {
      setResults([]);
    }
  };

  const assign = async (messageId: number, memberId: number) => {
    setAssigning(true);
    try {
      await inboxEmailAPI.assign(messageId, memberId);
      setItems((prev) => prev.filter((i) => i.id !== messageId));
      setActiveAssign(null);
      setQuery('');
      setResults([]);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to assign');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  }

  if (!mailbox?.connected) {
    return (
      <div className="p-6 text-center">
        <Mail className="w-10 h-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-600">No personal mailbox connected.</p>
        <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
          Connect Outlook to sync your member email here and send as yourself.
        </p>
        <button
          onClick={connectOutlook}
          disabled={connecting}
          className="mt-3 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
        >
          <Mail className="w-3.5 h-3.5" />
          {connecting ? 'Opening Microsoft…' : 'Connect Outlook'}
        </button>
        <p className="text-[11px] text-gray-400 mt-2 max-w-sm mx-auto">
          Asks only for mail access (read and send). Calendar and contacts are requested
          separately from Settings &rarr; Connected Services.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-600">
          Synced from <span className="font-medium">{mailbox.email}</span> · {items.length} need assignment
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-1 text-xs px-2 py-1 border rounded hover:bg-gray-50"
        >
          <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
          Sync now
        </button>
      </div>

      {items.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500">
          <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
          Nothing to assign — all synced email is linked to members.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((email) => (
            <div key={email.id} className="border rounded p-3 bg-white">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{email.subject || '(no subject)'}</p>
                  <p className="text-xs text-gray-500 truncate">
                    From: {email.from_name || email.from_email}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{email.snippet}</p>
                </div>
                <button
                  onClick={() => setActiveAssign(activeAssign === email.id ? null : email.id)}
                  className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 shrink-0"
                >
                  <UserPlus className="w-3 h-3" />
                  Assign
                </button>
              </div>

              {activeAssign === email.id && (
                <div className="mt-3 border-t pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="w-3 h-3 text-gray-400" />
                    <input
                      autoFocus
                      value={query}
                      onChange={(e) => searchMembers(e.target.value)}
                      placeholder="Search member by name or email…"
                      className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                    />
                  </div>
                  {results.length > 0 && (
                    <div className="max-h-48 overflow-y-auto border rounded divide-y">
                      {results.map((m) => (
                        <button
                          key={m.id}
                          disabled={assigning}
                          onClick={() => assign(email.id, m.id)}
                          className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-50 flex justify-between"
                        >
                          <span>{[m.first_name, m.last_name].filter(Boolean).join(' ') || `Member #${m.id}`}</span>
                          <span className="text-xs text-gray-400">{m.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailNeedsAssignment;
